import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '../../../../lib/supabase-auth'
import { useAuth } from '../../../../contexts/AuthContextLazy'
import { showError, showSuccess } from '../../../../utils/toast'
import { MyMeetingsModeIndicator, MyMeetingsModeMessage } from '../../../../components/integrations/MyMeetingsModeIndicator'
import { 
  ArrowLeftIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  LinkIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  CogIcon,
  PlayIcon,
  PauseIcon,
  ArrowsRightLeftIcon
} from '@heroicons/react/24/outline'

interface IntegrationCredentials {
  id: string
  integration_type: string
  credentials: {
    access_token: string
    refresh_token: string
    mymeetings_org_id: string
    mymeetings_org_name: string
    scopes: string[]
    expires_at: string
  }
  sync_stats?: {
    clients_synced: number
    meetings_synced: number
    action_items: number
    last_sync_at: string
    next_sync_at: string
  }
  sync_settings?: {
    frequency: 'realtime' | 'hourly' | 'daily'
    auto_sync: boolean
    sync_meetings: boolean
    sync_action_items: boolean
    sync_client_updates: boolean
  }
  created_at: string
  updated_at: string
}

interface ClientMapping {
  id: string
  portal_client_id: string
  portal_client_name: string
  mymeetings_party_id: string
  mymeetings_party_name: string
  confidence_score: number
  mapped_at: string
  mapped_by: string
}

interface SyncActivity {
  id: string
  timestamp: string
  action: string
  details: string
  status: 'success' | 'warning' | 'error'
}

export const MyMeetingsIntegration: React.FC = () => {
  const navigate = useNavigate()
  const { hasRole, user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [credentials, setCredentials] = useState<IntegrationCredentials | null>(null)
  const [mappings, setMappings] = useState<ClientMapping[]>([])
  const [recentActivity, setRecentActivity] = useState<SyncActivity[]>([])
  const [testingConnection, setTestingConnection] = useState(false)

  useEffect(() => {
    loadIntegrationData()
  }, [])

  const loadIntegrationData = async () => {
    try {
      // Load credentials
      const { data: creds } = await supabase
        .from('integration_credentials')
        .select('*')
        .eq('integration_type', 'mymeetings')
        .single()

      if (creds) {
        setCredentials(creds)

        // Load client mappings
        const { data: mappingData } = await supabase
          .from('client_mymeetings_mapping')
          .select(`
            *,
            organizations!portal_client_id(name)
          `)
          .limit(10)

        if (mappingData) {
          setMappings(mappingData.map(m => ({
            ...m,
            portal_client_name: m.organizations?.name || 'Unknown'
          })))
        }

        // Load recent sync activity
        const { data: activityData } = await supabase
          .from('integration_sync_logs')
          .select('*')
          .eq('integration_type', 'mymeetings')
          .order('timestamp', { ascending: false })
          .limit(10)

        if (activityData) {
          setRecentActivity(activityData)
        }
      }
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error loading integration data:', error);
        }

      }
    } finally {
      setLoading(false)
    }
  }

  const handleDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect MyMeetings? This will stop all syncing but won\'t delete existing data.')) {
      return
    }

    try {
      await supabase
        .from('integration_credentials')
        .delete()
        .eq('integration_type', 'mymeetings')

      navigate('/portal/admin/integrations')
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error disconnecting:', error);
        }

      }
      showError('Failed to disconnect MyMeetings')
    }
  }

  const handleTestConnection = async () => {
    setTestingConnection(true)
    try {
      // In a real implementation, this would call the MyMeetings API
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      // Log the test
      await supabase
        .from('integration_sync_logs')
        .insert({
          integration_type: 'mymeetings',
          action: 'test_connection',
          details: 'Connection test successful',
          status: 'success',
          user_id: user?.id
        })

      showSuccess('Connection test successful!')
      loadIntegrationData()
    } catch (error) {
      showError('Connection test failed')
    } finally {
      setTestingConnection(false)
    }
  }

  const handleManualSync = async () => {
    setSyncing(true)
    try {
      // In a real implementation, this would trigger the sync process
      await new Promise(resolve => setTimeout(resolve, 3000))
      
      // Update sync stats
      await supabase
        .from('integration_credentials')
        .update({
          sync_stats: {
            ...credentials?.sync_stats,
            last_sync_at: new Date().toISOString(),
            clients_synced: (credentials?.sync_stats?.clients_synced || 0) + 2,
            meetings_synced: (credentials?.sync_stats?.meetings_synced || 0) + 5,
            action_items: (credentials?.sync_stats?.action_items || 0) + 3
          }
        })
        .eq('integration_type', 'mymeetings')

      // Log the sync
      await supabase
        .from('integration_sync_logs')
        .insert({
          integration_type: 'mymeetings',
          action: 'manual_sync',
          details: 'Manual sync completed: 2 clients, 5 meetings, 3 action items',
          status: 'success',
          user_id: user?.id
        })

      showSuccess('Sync completed successfully!')
      loadIntegrationData()
    } catch (error) {
      showError('Sync failed')
    } finally {
      setSyncing(false)
    }
  }

  const toggleAutoSync = async () => {
    if (!credentials) return

    const newAutoSync = !credentials.sync_settings?.auto_sync
    
    try {
      await supabase
        .from('integration_credentials')
        .update({
          sync_settings: {
            ...credentials.sync_settings,
            auto_sync: newAutoSync
          }
        })
        .eq('integration_type', 'mymeetings')

      setCredentials({
        ...credentials,
        sync_settings: {
          ...credentials.sync_settings,
          auto_sync: newAutoSync
        }
      })
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error toggling auto-sync:', error);
        }

      }
    }
  }

  if (!hasRole(['super_admin', 'pcm_staff'])) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">You don't have permission to view this page.</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="h-8 w-8 text-gray-400 animate-spin" />
      </div>
    )
  }

  if (!credentials) {
    return (
      <div className="space-y-6">
        <div>
          <Link
            to="/portal/admin/integrations"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Integrations
          </Link>
          <h1 className="mt-4 text-2xl font-semibold text-gray-900">MyMeetings Integration</h1>
        </div>

        <div className="bg-white portal-content-shadow-sm rounded-lg border border-gray-200 p-8 text-center">
          <img 
            src="/assets/integrations/mymeetings-logo.png" 
            alt="MyMeetings logo"
            className="mx-auto h-12 w-auto"
          />
          <h3 className="mt-4 text-lg font-medium text-gray-900">Not Connected</h3>
          <p className="mt-2 text-sm text-gray-500">
            Connect your MyMeetings account to start syncing client data
          </p>
          <div className="mt-6">
            <Link
              to="/portal/admin/integrations/mymeetings/connect"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-amber-600 hover:bg-amber-700"
            >
              <LinkIcon className="mr-2 h-4 w-4" />
              Connect MyMeetings
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const syncSettings = credentials.sync_settings || {
    frequency: 'hourly',
    auto_sync: true,
    sync_meetings: true,
    sync_action_items: true,
    sync_client_updates: true
  }

  const syncStats = credentials.sync_stats || {
    clients_synced: 0,
    meetings_synced: 0,
    action_items: 0
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to="/portal/admin/integrations"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="mr-2 h-4 w-4" />
          Back to Integrations
        </Link>
        <div className="mt-4 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-gray-900">MyMeetings Integration</h1>
              <MyMeetingsModeIndicator />
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Connected to {credentials.credentials.mymeetings_org_name}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              <CheckCircleIcon className="mr-1.5 h-4 w-4" />
              Connected
            </span>
          </div>
        </div>
      </div>

      {/* Mock Mode Message */}
      <MyMeetingsModeMessage />

      {/* Stats Overview */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserGroupIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Synced Clients
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {syncStats.clients_synced}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3">
            <Link
              to="/portal/admin/integrations/mymeetings/mapping"
              className="text-sm text-amber-600 hover:text-amber-900"
            >
              Manage mappings
            </Link>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalendarDaysIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Recent Meetings
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {syncStats.meetings_synced}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3">
            <span className="text-sm text-gray-500">Last 30 days</span>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClipboardDocumentListIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Action Items
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {syncStats.action_items}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3">
            <span className="text-sm text-gray-500">Active items</span>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Sync Health
                  </dt>
                  <dd className="text-lg font-medium text-green-600">
                    Healthy
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3">
            <span className="text-sm text-gray-500">
              {syncStats.last_sync_at ? 
                `Last sync: ${new Date(syncStats.last_sync_at).toLocaleTimeString()}` : 
                'Never synced'
              }
            </span>
          </div>
        </div>
      </div>

      {/* Sync Controls */}
      <div className="bg-white portal-content-shadow-sm rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Sync Settings</h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Automatic Sync</h4>
              <p className="text-sm text-gray-500">
                Automatically sync data every {syncSettings.frequency === 'realtime' ? 'few minutes' : syncSettings.frequency}
              </p>
            </div>
            <button
              onClick={toggleAutoSync}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 ${
                syncSettings.auto_sync ? 'bg-amber-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  syncSettings.auto_sync ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="pt-4 space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={syncSettings.sync_meetings}
                className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                readOnly
              />
              <span className="ml-2 text-sm text-gray-700">Sync meeting summaries</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={syncSettings.sync_action_items}
                className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                readOnly
              />
              <span className="ml-2 text-sm text-gray-700">Sync action items</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={syncSettings.sync_client_updates}
                className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                readOnly
              />
              <span className="ml-2 text-sm text-gray-700">Sync client profile updates</span>
            </label>
          </div>

          <div className="pt-4 flex space-x-3">
            <button
              onClick={handleManualSync}
              disabled={syncing}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50"
            >
              {syncing ? (
                <>
                  <ArrowPathIcon className="mr-2 h-4 w-4 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <ArrowPathIcon className="mr-2 h-4 w-4" />
                  Sync Now
                </>
              )}
            </button>
            <button
              onClick={handleTestConnection}
              disabled={testingConnection}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
            >
              {testingConnection ? (
                <>
                  <ArrowPathIcon className="mr-2 h-4 w-4 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <PlayIcon className="mr-2 h-4 w-4" />
                  Test Connection
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Client Mappings Preview */}
      <div className="bg-white portal-content-shadow-sm rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Client Mappings</h3>
          <Link
            to="/portal/admin/integrations/mymeetings/mapping"
            className="text-sm text-amber-600 hover:text-amber-900"
          >
            View all mappings
          </Link>
        </div>
        <div className="overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Portal Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  MyMeetings Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Confidence
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {mappings.slice(0, 5).map((mapping) => (
                <tr key={mapping.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {mapping.portal_client_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {mapping.mymeetings_party_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                        <div
                          className="bg-green-500 h-2 rounded-full"
                          style={{ width: `${mapping.confidence_score * 100}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-500">
                        {Math.round(mapping.confidence_score * 100)}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white portal-content-shadow-sm rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
        </div>
        <div className="overflow-hidden">
          <ul className="divide-y divide-gray-200">
            {recentActivity.map((activity) => (
              <li key={activity.id} className="px-6 py-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    {activity.status === 'success' && (
                      <CheckCircleIcon className="h-5 w-5 text-green-500" />
                    )}
                    {activity.status === 'warning' && (
                      <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />
                    )}
                    {activity.status === 'error' && (
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                    )}
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm text-gray-900">{activity.action}</p>
                    <p className="text-sm text-gray-500">{activity.details}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(activity.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-white portal-content-shadow-sm rounded-lg border border-red-200">
        <div className="px-6 py-4 border-b border-red-200 bg-red-50">
          <h3 className="text-lg font-medium text-red-900">Danger Zone</h3>
        </div>
        <div className="p-6">
          <p className="text-sm text-gray-600 mb-4">
            Disconnecting MyMeetings will stop all automatic syncing. Your existing data will remain,
            but no new data will be synced until you reconnect.
          </p>
          <button
            onClick={handleDisconnect}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            Disconnect MyMeetings
          </button>
        </div>
      </div>
    </div>
  )
}